import React from "react";
import { type Tenancy } from "../../api";
import { useLanguage } from "../../i18n";
import { FURNISHING_ITEM_KEYS } from "../../furnishingItems";

function fmtINR(n: number) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

// Read-only recap of a tenant's move-in details (rent, deposit, amenities) —
// reachable any time from the residence card on TenantHome, not just during
// the one-time onboarding flow (see TenantOnboarding.tsx, which shows the
// same amenities data but as part of the blocking move-in confirmation).
export function MoveInDetails({ tenancy, onBack }: { tenancy: Tenancy; onBack: () => void }) {
  const { t } = useLanguage();

  const unit = tenancy.unitId ? tenancy.property?.units?.find((u) => u.id === tenancy.unitId) ?? null : null;
  const facilities = tenancy.property?.facilities ?? [];
  const furnishingStatus = unit?.furnishingStatus ?? tenancy.property?.furnishingStatus ?? null;
  const communityType = unit?.communityType ?? tenancy.property?.communityType ?? null;
  const floorNumber = unit?.floorNumber ?? tenancy.property?.floorNumber ?? null;
  const furnishingItemEntries = Object.entries(unit?.furnishingItems ?? tenancy.property?.furnishingItems ?? {}).filter(
    ([, count]) => Number(count) > 0
  );

  return (
    <div style={{ padding: 16 }}>
      <button className="link-btn" style={{ marginBottom: 14 }} onClick={onBack}>
        {t("rentalAgreement.back")}
      </button>
      <div className="reg-title">{t("moveInDetails.title")}</div>
      <div className="reg-sub">{tenancy.property?.label}</div>

      <div className="card">
        <div className="card-row">
          <span className="sub">{t("tenantOnboarding.rentLabel")}</span>
          <b>{fmtINR(tenancy.rent)}</b>
        </div>
        <div className="card-row" style={{ marginTop: 8 }}>
          <span className="sub">{t("tenantOnboarding.dueDayLabel")}</span>
          <b>{t("tenantOnboarding.dueDayValue", { day: tenancy.rentDueDay })}</b>
        </div>
      </div>

      <div className="card">
        <div className="card-row">
          <div>
            <div className="sub">{t("tenantOnboarding.depositLabel")}</div>
            <b>{fmtINR(tenancy.deposit)}</b>
          </div>
          {tenancy.depositStatus === "paid" ? (
            <span className="badge paid">
              {tenancy.depositPaymentMode === "cash" ? t("tenantOnboarding.depositPaidCashBadge") : t("tenantOnboarding.depositPaidBadge")}
            </span>
          ) : (
            <span className="badge pending">{t("tenantOnboarding.depositPendingBadge")}</span>
          )}
        </div>
      </div>

      {(facilities.length > 0 || furnishingStatus || communityType || furnishingItemEntries.length > 0) && (
        <div className="card">
          <div className="sub" style={{ marginBottom: 8 }}>{t("tenantOnboarding.amenitiesLabel")}</div>
          {(furnishingStatus || communityType) && (
            <div className="chip-row" style={{ marginBottom: facilities.length || furnishingItemEntries.length ? 10 : 0 }}>
              {furnishingStatus && <div className="chip selected">{t(`editPropertyForm.furnishing_${furnishingStatus}`)}</div>}
              {communityType && <div className="chip selected">{t(`editPropertyForm.community_${communityType}`)}</div>}
              {floorNumber && <div className="chip selected">{t("editPropertyForm.floorChipLabel", { floor: floorNumber })}</div>}
            </div>
          )}
          {furnishingItemEntries.length > 0 && (
            <div className="chip-row" style={{ marginBottom: facilities.length ? 10 : 0 }}>
              {furnishingItemEntries.map(([key, count]) => (
                <div className="chip selected" key={key}>
                  {count}× {(FURNISHING_ITEM_KEYS as readonly string[]).includes(key) ? t(`editPropertyForm.item_${key}`) : key}
                </div>
              ))}
            </div>
          )}
          {facilities.length > 0 && (
            <div className="chip-row">
              {facilities.map((f) => (
                <div className="chip selected" key={f}>
                  {f}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tenancy.property?.ownerName && (
        <div className="card">
          <div className="card-row">
            <div>
              <div className="sub">{t("tenantHome.ownerLabel")}</div>
              <b>{tenancy.property.ownerName}</b>
            </div>
            {tenancy.property.ownerPhone && (
              <a className="btn-outline btn-sm" href={`tel:${tenancy.property.ownerPhone}`}>
                {tenancy.property.ownerPhone}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
